import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { getCachedSignedUrl } from '../../utils/storage';
import AchievementBadge from '../user-profile-dashboard/components/AchievementBadge';
import ProjectCard from '../project-dashboard/components/ProjectCard';
import StatisticsCard from '../user-profile-dashboard/components/StatisticsCard';
import StreakCounter from '../user-profile-dashboard/components/StreakCounter';
import ProofHeader from '../proof-detail-view/components/ProofHeader';
import ProofImageViewer from '../proof-detail-view/components/ProofImageViewer';
import ProofMetadata from '../proof-detail-view/components/ProofMetadata';

const PeopleUser = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('id') || '';
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'proofs' || tab === 'projects' || tab === 'achievements') return tab;
    return 'projects';
  });

  const [projects, setProjects] = useState([]);

  const [proofs, setProofs] = useState([]);
  const [achievements, setAchievements] = useState([]);

  const [isProofDetailOpen, setIsProofDetailOpen] = useState(false);
  const [activeProofIndex, setActiveProofIndex] = useState(0);

  const [statistics, setStatistics] = useState({
    totalProjects: 0,
    completedChallenges: 0,
    submittedProofs: 0,
    averageStars: 0,
    receivedLikes: 0,
  });
  const [score, setScore] = useState(0);
  const [neighborCount, setNeighborCount] = useState(0);
  const [isNeighbor, setIsNeighbor] = useState(false);
  const [isTogglingNeighbor, setIsTogglingNeighbor] = useState(false);
  const [streakData, setStreakData] = useState({
    currentStreak: 0,
    longestStreak: 0,
    lastActivityDate: null,
  });

  const tabs = useMemo(
    () => [
      { id: 'projects', label: '목표', icon: 'LayoutGrid' },
      { id: 'proofs', label: '증명', icon: 'Image' },
      { id: 'achievements', label: '업적', icon: 'Award' },
    ],
    []
  );

  const computeStreaks = (dates) => {
    const uniqueDays = Array.from(
      new Set(
        (dates || [])
          .filter(Boolean)
          .map((d) => new Date(d).toISOString().slice(0, 10))
      )
    ).sort();

    if (uniqueDays.length === 0) {
      return { currentStreak: 0, longestStreak: 0, lastActivityDate: null };
    }

    const asDayNumber = (isoDay) => Math.floor(new Date(isoDay + 'T00:00:00Z').getTime() / 86400000);
    const dayNums = uniqueDays.map(asDayNumber);

    let longest = 1;
    let currentRun = 1;
    for (let i = 1; i < dayNums.length; i += 1) {
      if (dayNums[i] === dayNums[i - 1] + 1) currentRun += 1;
      else {
        longest = Math.max(longest, currentRun);
        currentRun = 1;
      }
    }
    longest = Math.max(longest, currentRun);

    let currentStreak = 1;
    for (let i = dayNums.length - 1; i > 0; i -= 1) {
      if (dayNums[i] === dayNums[i - 1] + 1) currentStreak += 1;
      else break;
    }

    return {
      currentStreak,
      longestStreak: longest,
      lastActivityDate: uniqueDays[uniqueDays.length - 1] + 'T00:00:00Z',
    };
  };

  const getLevelInfo = (scoreValue) => {
    const thresholds = [0, 100, 300, 600, 1000];
    let level = 1;
    let current = 0;
    let next = 100;

    for (let i = thresholds.length - 1; i >= 0; i -= 1) {
      if (scoreValue >= thresholds[i]) {
        level = i + 1;
        current = thresholds[i];
        next = thresholds[i + 1] ?? (thresholds[i] + 500);
        break;
      }
    }

    const progress = next > current
      ? Math.min(100, ((scoreValue - current) / (next - current)) * 100)
      : 100;

    return { level, current, next, progress };
  };

  const levelInfo = useMemo(() => getLevelInfo(score), [score]);
  const lastAttendanceText = useMemo(() => {
    if (!streakData.lastActivityDate) return '기록 없음';
    return new Date(streakData.lastActivityDate).toLocaleDateString('ko-KR');
  }, [streakData.lastActivityDate]);

  const load = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const [{ data: p }, { data: proj }, { data: pr }, uaRes, { data: ch }, { data: receivedLikesCount }]
        = await Promise.all([
          supabase.from('ch_profiles').select('id, username, avatar_url, is_public, score').eq('id', userId).maybeSingle(),
          supabase
            .from('ch_projects')
            .select('id, title, description, created_at, cover_image_path, cover_image_alt, total_challenges, completed_challenges, completed_proofs, average_stars, is_archived, status')
            .eq('owner_id', userId)
            .eq('is_public', true)
            .order('created_at', { ascending: false }),
          supabase
            .from('ch_proofs')
            .select('id, number, title, description, rating, submitted_at, image_path, youtube_url, is_public, challenge:ch_challenges(id, title, project_id)')
            .eq('user_id', userId)
            .eq('is_public', true)
            .order('submitted_at', { ascending: false })
            .limit(60),
          supabase
            .from('ch_user_achievements')
            .select('unlocked_at, achievement:ch_achievements(id, name, description, icon, criteria)')
            .eq('user_id', userId)
            .order('unlocked_at', { ascending: false }),
          supabase
            .from('ch_challenges')
            .select('id, project_id, goal_type, target_count, total_goals')
            .eq('owner_id', userId)
            .eq('is_public', true),
          supabase.rpc('ch_get_public_received_likes_count', { p_user_id: userId }),
        ]);

      if (!p || p.is_public !== true) {
        setProfile(null);
        setProjects([]);
        setProofs([]);
        setAchievements([]);
        setStatistics({ totalProjects: 0, completedChallenges: 0, submittedProofs: 0, averageStars: 0, receivedLikes: 0 });
        setStreakData({ currentStreak: 0, longestStreak: 0, lastActivityDate: null });
        return;
      }

      setProfile({
        id: p.id,
        username: p.username,
        avatarUrl: p.avatar_url || '',
      });
      setScore(Number(p.score || 0));

      const [{ data: neighborCountValue }, { data: neighborRow }] = await Promise.all([
        supabase.rpc('ch_get_neighbored_by_count', { p_user_id: userId }),
        user?.id
          ? supabase
            .from('ch_neighbors')
            .select('user_id')
            .eq('user_id', user.id)
            .eq('neighbor_id', userId)
            .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      setNeighborCount(Number(neighborCountValue || 0));
      setIsNeighbor(!!neighborRow?.user_id);

      const challenges = ch || [];
      const proofs = pr || [];

      const proofCountByChallengeId = new Map();
      const ratingSumByChallengeId = new Map();
      const ratingCountByChallengeId = new Map();
      proofs.forEach((r) => {
        const cid = r?.challenge?.id;
        if (!cid) return;
        proofCountByChallengeId.set(cid, (proofCountByChallengeId.get(cid) || 0) + 1);

        const rating = Number(r?.rating);
        if (Number.isFinite(rating) && rating > 0) {
          ratingSumByChallengeId.set(cid, (ratingSumByChallengeId.get(cid) || 0) + rating);
          ratingCountByChallengeId.set(cid, (ratingCountByChallengeId.get(cid) || 0) + 1);
        }
      });

      const projectStats = new Map();
      challenges.forEach((c) => {
        const pid = c?.project_id;
        if (!pid) return;
        const cur = projectStats.get(pid) || {
          totalChallenges: 0,
          completedChallenges: 0,
          completedProofs: 0,
          totalLikes: 0,
          ratingSum: 0,
          ratingCount: 0,
        };

        cur.totalChallenges += 1;
        const done = proofCountByChallengeId.get(c.id) || 0;
        cur.completedProofs += done;

        const goalType = c.goal_type === 'thirty-day' ? 'thirty_day' : c.goal_type;
        const total = goalType === 'thirty_day' ? 30 : Number(c.total_goals ?? c.target_count ?? 0);
        if (total > 0 && done >= total) cur.completedChallenges += 1;

        const rs = ratingSumByChallengeId.get(c.id) || 0;
        const rc = ratingCountByChallengeId.get(c.id) || 0;
        cur.ratingSum += rs;
        cur.ratingCount += rc;

        projectStats.set(pid, cur);
      });

      const { data: likeSums } = await supabase.rpc('ch_list_public_project_like_sums', { p_owner_id: userId });
      const likesByProjectId = new Map();
      (likeSums || []).forEach((r) => {
        likesByProjectId.set(r.project_id, Number(r.total_likes || 0));
      });

      const projVm = await Promise.all(
        (proj || []).map(async (x) => {
          const coverUrl = x.cover_image_path
            ? await getCachedSignedUrl({ bucket: 'project-covers', path: x.cover_image_path })
            : '';

          const stat = projectStats.get(x.id);
          const averageStars = stat && stat.ratingCount > 0 ? stat.ratingSum / stat.ratingCount : null;

          return {
            id: x.id,
            title: x.title,
            description: x.description || '',
            coverImage: coverUrl,
            coverImageAlt: x.cover_image_alt || x.title,
            totalChallenges: stat?.totalChallenges ?? x.total_challenges ?? 0,
            completedChallenges: stat?.completedChallenges ?? x.completed_challenges ?? 0,
            completedProofs: stat?.completedProofs ?? x.completed_proofs ?? 0,
            totalLikes: likesByProjectId.get(x.id) ?? 0,
            averageStars: Number(
              (averageStars ?? x.average_stars ?? 0)
            ),
            isArchived: x.is_archived ?? false,
            createdAt: x.created_at,
            status: x.status || 'active',
          };
        })
      );
      setProjects(projVm);

      const getYoutubeVideoId = (rawUrl) => {
        const url = String(rawUrl || '').trim();
        if (!url) return '';
        const directId = url.match(/^[a-zA-Z0-9_-]{11}$/)?.[0];
        if (directId) return directId;
        try {
          const u = new URL(url);
          const host = (u.hostname || '').replace(/^www\./, '');
          if (host === 'youtu.be') return (u.pathname || '').replace('/', '').slice(0, 11);
          const v = u.searchParams.get('v');
          if (v) return v.slice(0, 11);
          const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
          if (m?.[1]) return String(m[1]).slice(0, 11);
          return '';
        } catch {
          return '';
        }
      };

      const getYoutubeThumb = (rawUrl) => {
        const id = getYoutubeVideoId(rawUrl);
        if (!id) return '';
        return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      };

      const mappedProofs = await Promise.all(
        (pr || []).map(async (p) => {
          const thumb = p.youtube_url ? getYoutubeThumb(p.youtube_url) : '';
          const url = p.image_path
            ? await getCachedSignedUrl({ bucket: 'proof-images', path: p.image_path })
            : (thumb || '');
          return {
            id: p.id,
            number: p.number,
            title: p.title || '',
            description: p.description || '',
            rating: Number(p.rating || 0),
            submittedAt: p.submitted_at ? String(p.submitted_at).slice(0, 10) : '',
            image: url,
            challengeId: p.challenge?.id,
            challengeTitle: p.challenge?.title || '',
            youtubeUrl: p.youtube_url || '',
          };
        })
      );
      setProofs(mappedProofs);

      const proofCount = (pr || []).length;
      const totalStars = (pr || []).reduce((sum, r) => sum + Number(r.rating || 0), 0);
      const avgStars = proofCount > 0 ? totalStars / proofCount : 0;
      const streak = computeStreaks((pr || []).map((r) => r.submitted_at));
      setStreakData(streak);

      const proofCountByChallenge = new Map();
      (proofs || []).forEach((r) => {
        const cid = r.challenge?.id;
        if (!cid) return;
        proofCountByChallenge.set(cid, (proofCountByChallenge.get(cid) || 0) + 1);
      });

      const completedChallengeCount = challenges.filter((c) => {
        const goalType = c.goal_type === 'thirty-day' ? 'thirty_day' : c.goal_type;
        const total = goalType === 'thirty_day' ? 30 : Number(c.total_goals ?? c.target_count ?? 0);
        const done = proofCountByChallenge.get(c.id) || 0;
        return total > 0 && done >= total;
      }).length;

      setStatistics({
        totalProjects: (proj || []).length,
        completedChallenges: completedChallengeCount,
        submittedProofs: proofCount,
        averageStars: Number(avgStars.toFixed(1)),
        receivedLikes: Number(receivedLikesCount || 0),
      });

      const ua = uaRes?.error ? [] : (uaRes?.data || []);
      const ach = (ua || []).map((x) => ({
        id: x.achievement?.id,
        name: x.achievement?.name,
        description: x.achievement?.description,
        icon: x.achievement?.icon,
        criteria: x.achievement?.criteria,
        unlocked: true,
        unlockedAt: x.unlocked_at,
      })).filter((x) => !!x.id);

      if (ach.length > 0) {
        setAchievements(ach);
      } else {
        const { data: allAchievements, error: allErr } = await supabase
          .from('ch_achievements')
          .select('id, name, description, icon, criteria')
          .order('created_at', { ascending: true });

        if (!allErr) {
          setAchievements(
            (allAchievements || []).map((a) => ({
              id: a.id,
              name: a.name,
              description: a.description,
              icon: a.icon,
              criteria: a.criteria,
              unlocked: false,
              unlockedAt: null,
            }))
          );
        } else {
          setAchievements([]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'proofs' || tab === 'projects' || tab === 'achievements') {
      setActiveTab(tab);
      return;
    }
    setActiveTab('projects');
  }, [searchParams]);

  const openProofDetailById = useCallback(
    (proofId) => {
      const idx = proofs?.findIndex((p) => p?.id === proofId);
      if (idx < 0) return;
      setActiveProofIndex(idx);
      setIsProofDetailOpen(true);
    },
    [proofs]
  );

  const handlePrevProof = useCallback(() => {
    setActiveProofIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleNextProof = useCallback(() => {
    setActiveProofIndex((i) => Math.min((proofs?.length || 1) - 1, i + 1));
  }, [proofs?.length]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-5 md:py-8 lg:py-12">
        {isLoading ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6">
            <p className="text-sm text-muted-foreground caption">로딩 중...</p>
          </div>
        ) : !profile ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground caption">공개 프로필을 찾을 수 없습니다.</p>
              <button
                type="button"
                onClick={() => navigate('/people')}
                className="px-4 py-2 rounded-md bg-muted/40 text-foreground hover:bg-muted transition-gaming"
              >
                목록으로
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-card rounded-lg shadow-gaming-md p-5 md:p-6 mb-6">
              <div className="flex items-center space-x-3 md:space-x-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-muted/40 border border-border overflow-hidden flex items-center justify-center">
                  {profile.avatarUrl ? (
                    <img src={profile.avatarUrl} alt={`${profile.username} avatar`} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="User" size={28} className="text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-foreground line-clamp-1">
                      {profile.username}
                    </h1>
                    {user?.id && profile?.id && user.id !== profile.id ? (
                      <button
                        type="button"
                        disabled={isTogglingNeighbor}
                        onClick={async () => {
                          if (!user?.id || !profile?.id) return;
                          if (isTogglingNeighbor) return;
                          setIsTogglingNeighbor(true);
                          try {
                            const { data, error } = await supabase.rpc('ch_toggle_neighbor', {
                              p_neighbor_id: profile.id,
                            });
                            if (error) return;

                            const nextIsNeighbor = !!data;
                            if (!isNeighbor && nextIsNeighbor) setNeighborCount((v) => v + 1);
                            if (isNeighbor && !nextIsNeighbor) setNeighborCount((v) => Math.max(0, v - 1));
                            setIsNeighbor(nextIsNeighbor);
                          } finally {
                            setIsTogglingNeighbor(false);
                          }
                        }}
                        className={`px-4 py-2 rounded-md border transition-gaming text-sm font-medium flex items-center gap-2 ${
                          isNeighbor
                            ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                            : 'bg-card text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        <Icon name={isNeighbor ? 'UserCheck' : 'UserPlus'} size={16} color="currentColor" />
                        {isNeighbor ? '이웃' : '이웃추가'}
                      </button>
                    ) : null}
                  </div>
                  <p className="text-sm text-muted-foreground caption mt-1">
                    공개 프로필
                  </p>
                </div>
              </div>

              <div className="sm:hidden bg-card rounded-lg shadow-gaming-md border border-border overflow-hidden mt-4">
                <div className="divide-y divide-border">
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-muted-foreground caption">전체 목표</span>
                    <span className="text-sm font-bold text-foreground data-text">{Number(statistics?.totalProjects || 0).toLocaleString('ko-KR')}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-muted-foreground caption">이웃</span>
                    <span className="text-sm font-bold text-foreground data-text">{Number(neighborCount || 0).toLocaleString('ko-KR')}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-muted-foreground caption">완료한 챌린지</span>
                    <span className="text-sm font-bold text-foreground data-text">{Number(statistics?.completedChallenges || 0).toLocaleString('ko-KR')}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-muted-foreground caption">제출한 증명</span>
                    <span className="text-sm font-bold text-foreground data-text">{Number(statistics?.submittedProofs || 0).toLocaleString('ko-KR')}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-muted-foreground caption">평균 별점</span>
                    <span className="text-sm font-bold text-foreground data-text">{Number(statistics?.averageStars || 0).toLocaleString('ko-KR')}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-muted-foreground caption">받은 좋아요</span>
                    <span className="text-sm font-bold text-foreground data-text">{Number(statistics?.receivedLikes || 0).toLocaleString('ko-KR')}</span>
                  </div>
                </div>
              </div>

              <div className="hidden sm:grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-6 mt-4">
                <StatisticsCard
                  icon="FolderOpen"
                  label="전체 목표"
                  value={statistics.totalProjects}
                  trend="neutral"
                  trendValue=""
                  color="primary"
                />
                <StatisticsCard
                  icon="Users"
                  label="이웃"
                  value={neighborCount}
                  trend="neutral"
                  trendValue=""
                  color="primary"
                />
                <StatisticsCard
                  icon="CheckCircle"
                  label="완료한 챌린지"
                  value={statistics.completedChallenges}
                  trend="neutral"
                  trendValue=""
                  color="success"
                />
                <StatisticsCard
                  icon="Image"
                  label="제출한 증명"
                  value={statistics.submittedProofs}
                  trend="neutral"
                  trendValue=""
                  color="accent"
                />
                <StatisticsCard
                  icon="Star"
                  label="평균 별점"
                  value={statistics.averageStars}
                  trend="neutral"
                  trendValue=""
                  color="warning"
                />
                <StatisticsCard
                  icon="Heart"
                  label="받은 좋아요"
                  value={statistics.receivedLikes}
                  trend="neutral"
                  trendValue=""
                  color="accent"
                />
              </div>

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="bg-card rounded-lg shadow-gaming-md p-4 border border-border">
                  <div className="text-sm font-semibold text-foreground mb-2">레벨</div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-md bg-primary/20 text-primary flex items-center justify-center font-bold">
                      Lv.{levelInfo.level}
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground caption">현재 점수</div>
                      <div className="text-lg font-bold text-foreground data-text">{score.toLocaleString('ko-KR')}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                      <span>{levelInfo.current}점</span>
                      <span>{levelInfo.next}점</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-gaming rounded-full"
                        style={{ width: `${levelInfo.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-card rounded-lg shadow-gaming-md p-4 border border-border lg:col-span-2">
                  <div className="text-sm font-semibold text-foreground mb-2">출석</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div className="rounded-md bg-muted/40 border border-border px-3 py-2">
                      <div className="text-xs text-muted-foreground caption">현재 연속</div>
                      <div className="text-lg font-bold text-foreground data-text">{streakData.currentStreak}일</div>
                    </div>
                    <div className="rounded-md bg-muted/40 border border-border px-3 py-2">
                      <div className="text-xs text-muted-foreground caption">최장 연속</div>
                      <div className="text-lg font-bold text-foreground data-text">{streakData.longestStreak}일</div>
                    </div>
                    <div className="rounded-md bg-muted/40 border border-border px-3 py-2">
                      <div className="text-xs text-muted-foreground caption">마지막 출석</div>
                      <div className="text-base font-semibold text-foreground data-text">{lastAttendanceText}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 mb-5 md:mb-6 overflow-x-auto">
              <div className="flex items-center space-x-2 min-w-max">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setActiveTab(t.id);
                      const qs = new URLSearchParams(searchParams);
                      qs.set('tab', t.id);
                      navigate(`/people/user?${qs.toString()}`);
                    }}
                    className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-gaming ${
                      activeTab === t.id
                        ? 'bg-primary text-primary-foreground shadow-gaming-sm'
                        : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={t.icon} size={18} />
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'projects' && (
              <div className="space-y-4">
                {projects.length === 0 ? (
                  <div className="bg-card rounded-lg shadow-gaming-md p-6">
                    <p className="text-sm text-muted-foreground caption">공개 목표가 없습니다.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {projects.map((p) => (
                      <div key={p.id}>
                        <ProjectCard project={p} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'proofs' && (
              <div>
                {proofs.length === 0 ? (
                  <div className="bg-card rounded-lg shadow-gaming-md p-6">
                    <p className="text-sm text-muted-foreground caption">공개 증명이 없습니다.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {proofs.map((p) => (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openProofDetailById(p.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') openProofDetailById(p.id);
                        }}
                        className="relative w-full aspect-square bg-card rounded-lg border border-border overflow-hidden shadow-gaming-sm"
                      >
                        <Image
                          src={p.image || '/assets/images/no_image.png'}
                          alt={p.challengeTitle || 'proof'}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-background/20" />
                        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3">
                          <div className="bg-card rounded-md border border-border p-2 md:p-3">
                            <div className="text-sm font-medium text-foreground line-clamp-1 mb-1">
                              {p.challengeTitle}
                            </div>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-primary/10 border border-primary/20">
                                {[...Array(5)]?.map((_, index) => (
                                  <Icon
                                    key={index}
                                    name="Star"
                                    size={14}
                                    color="currentColor"
                                    className={index < (p?.rating || 0) ? 'fill-current text-primary' : 'text-muted-foreground'}
                                  />
                                ))}
                              </div>
                              <span className="text-xs text-muted-foreground caption">#{p.number}</span>
                            </div>

                            {(p?.title || p?.description) && (
                              <div className="rounded-md border border-border/70 bg-card px-2 py-2">
                                {p?.title && (
                                  <div className="text-xs font-medium text-foreground line-clamp-1">
                                    {p.title}
                                  </div>
                                )}
                                {p?.description && (
                                  <div className="text-[11px] text-muted-foreground caption line-clamp-2 mt-1">
                                    {p.description}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'achievements' && (
              <div>
                {achievements.length === 0 ? (
                  <div className="bg-card rounded-lg shadow-gaming-md p-6">
                    <p className="text-sm text-muted-foreground caption">획득한 업적이 없습니다.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {achievements.map((a) => (
                      <AchievementBadge key={a.id} badge={a} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {isProofDetailOpen && proofs?.[activeProofIndex] && (
              <div
                className="fixed inset-0 z-dialog bg-background/80 flex items-center justify-center p-4"
                onClick={() => setIsProofDetailOpen(false)}
              >
                <div className="w-full max-w-4xl" onClick={(e) => e?.stopPropagation()}>
                  <div className="bg-card rounded-lg shadow-gaming-lg p-4 md:p-6 lg:p-8">
                    <ProofHeader
                      currentProof={activeProofIndex + 1}
                      totalProofs={proofs?.length}
                      challengeTitle={proofs?.[activeProofIndex]?.challengeTitle}
                      onClose={() => setIsProofDetailOpen(false)}
                    />

                    <div className="space-y-6 md:space-y-8">
                      <ProofImageViewer
                        imageUrl={proofs?.[activeProofIndex]?.image}
                        imageAlt={proofs?.[activeProofIndex]?.challengeTitle || 'proof'}
                        onPrevious={handlePrevProof}
                        onNext={handleNextProof}
                        hasPrevious={activeProofIndex > 0}
                        hasNext={activeProofIndex < (proofs?.length || 0) - 1}
                      />

                      <ProofMetadata
                        submissionDate={proofs?.[activeProofIndex]?.submittedAt}
                        starRating={proofs?.[activeProofIndex]?.rating}
                        description={proofs?.[activeProofIndex]?.description}
                        youtubeUrl={proofs?.[activeProofIndex]?.youtubeUrl || ''}
                      />

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setIsProofDetailOpen(false)}
                          className="px-4 py-2 rounded-md bg-muted/40 text-foreground hover:bg-muted transition-gaming"
                        >
                          닫기
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PeopleUser;
